/**
 * The shell: four permanently mounted roots, one visible.
 *
 * The single rule that shapes this file is F-NAV-03 — *"a router that unmounts
 * routes is a parity regression"*. So there is no `<Routes>` switch anywhere
 * near the destinations. All four roots render on the first commit and stay
 * rendered; selection changes which one is visible, hit-testable and exposed to
 * assistive technology, and nothing else.
 *
 * The chrome is chosen from the resolved layout rather than from a media query
 * of its own: `@ff/ui`'s ladder already encodes the 620 px split boundary, the
 * 1000 px auto-show rule and the accessibility collapse, and a second copy of
 * those numbers here is how a shell drifts off the ladder the features are
 * still on.
 */

import type { ComponentType, ReactNode } from "react";
import { Suspense, lazy, useEffect, useMemo, useState } from "react";
import { useBreakpoint } from "@ff/ui";
import { useShellActivity } from "../app-state/activity";
import { useNavigation } from "../app-state/navigation";
import { useShortcut } from "../app-state/shortcuts";
// The path only — `presentation.ts` exists so naming the live surface does not
// drag the lazily loaded route into the shell's own chunk.
import { LIVE_ROUTE_PATH } from "../features/train/live/presentation";
import { Dock } from "./Dock";
import { DestinationRoot } from "./DestinationRoot";
import { RouteFallback } from "./RouteFallback";
import { NEW_ENTRY_SHORTCUT, Sidebar } from "./Sidebar";
import type { Destination, DestinationId } from "./destinations";
import { DESTINATIONS, WORDMARK_EYEBROW, WORDMARK_TITLE } from "./destinations";
import { destinationRootEntry, fullViewportEntries, fullViewportEntry } from "./routeTable";
import type { BuildTool } from "./sidebarModel";
import {
  initialSidebarVisibility,
  reconcileSidebarVisibility,
  toggleSidebarVisibility,
} from "./sidebarModel";
import styles from "./AppShell.module.css";

/**
 * Built once at module scope. `lazy` memoises on the component identity, so
 * rebuilding these per render would re-suspend — and re-mount — every root on
 * every commit, which is the exact failure F-NAV-03 forbids.
 */
const DESTINATION_ROOTS: Readonly<Record<DestinationId, ComponentType>> = Object.freeze({
  train: lazy(destinationRootEntry("train").load),
  build: lazy(destinationRootEntry("build").load),
  journal: lazy(destinationRootEntry("journal").load),
  settings: lazy(destinationRootEntry("settings").load),
});

const FULL_VIEWPORT_ROOTS: Readonly<Record<string, ComponentType>> = Object.freeze(
  Object.fromEntries(fullViewportEntries().map((entry) => [entry.path, lazy(entry.load)])),
);

/**
 * F-NAV-08: Ringside is an action; the surface it opens is the live route.
 *
 * Named, then checked against the table — not derived from the table's order.
 * The earlier "first `fullViewport` row" form silently repointed this action at
 * `/settings/drills` the moment the drill library was registered in sorted
 * position ahead of `/train/live`.
 */
const RINGSIDE_PATH = fullViewportEntry(LIVE_ROUTE_PATH).path;

/** §9.2 remap of the sidebar toggle; ⌘S is reserved by every browser's Save. */
const SIDEBAR_TOGGLE_SHORTCUT = { key: "s", ctrl: true, alt: true } as const;

interface DestinationShortcutProps {
  readonly destination: Destination;
  readonly onSelect: (destination: DestinationId, tool?: BuildTool) => void;
}

/**
 * One component per binding rather than a loop of `useShortcut` calls: the hook
 * order stays fixed by construction instead of by a promise that the array
 * length never changes.
 */
function DestinationShortcut({ destination, onSelect }: DestinationShortcutProps): null {
  useShortcut(destination.shortcut, () => {
    // F-NAV-13: the Build shortcut also forces the tool back to Workouts, so
    // the keyboard always lands somewhere predictable rather than on whichever
    // tool was last open.
    onSelect(destination.id, destination.id === "build" ? "workouts" : undefined);
  });
  return null;
}

export function AppShell(): ReactNode {
  const layout = useBreakpoint();
  const navigation = useNavigation();
  const activity = useShellActivity();

  const automatic = layout.sidebar === "auto";
  const [sidebar, setSidebar] = useState(() => initialSidebarVisibility(automatic));

  useEffect(() => {
    // F-NAV-12's guard: a manual toggle sticks until the *computed* mode
    // actually flips. Reapplying the rule unconditionally would undo the user's
    // choice on the next resize of a single pixel.
    setSidebar((current) => reconcileSidebarVisibility(current, automatic));
  }, [automatic]);

  const { select, selectBuildTool } = navigation;
  const onSelect = useMemo(
    () =>
      (destination: DestinationId, tool?: BuildTool): void => {
        select(destination);
        if (tool !== undefined) selectBuildTool(tool);
      },
    [select, selectBuildTool],
  );

  const { presentFullViewport } = navigation;
  const onRingside = useMemo(
    () => () => {
      presentFullViewport(RINGSIDE_PATH);
    },
    [presentFullViewport],
  );

  useShortcut(SIDEBAR_TOGGLE_SHORTCUT, () => {
    setSidebar(toggleSidebarVisibility);
  });

  const createJournalEntry = activity.createJournalEntry;
  useShortcut(
    NEW_ENTRY_SHORTCUT,
    () => {
      createJournalEntry?.();
    },
    // F-NAV-13: ⌘N is Journal-only. Registered unconditionally and gated here
    // so the binding's position in the "last registered wins" order does not
    // shuffle every time the user changes destination.
    { enabled: navigation.destination === "journal" && createJournalEntry !== null },
  );

  const showsDock = layout.sidebar === "dock";
  const showsSidebar = !showsDock && sidebar.visible;
  const fullViewportPath = navigation.fullViewportPath;
  const FullViewport =
    fullViewportPath === null ? undefined : FULL_VIEWPORT_ROOTS[fullViewportPath];
  // The overlay covers the shell; the shell underneath must leave the tab order
  // and the accessibility tree, or the dock stays reachable behind a workout.
  const covered = FullViewport !== undefined;

  return (
    <div className={styles.shell} data-ff-chrome={showsDock ? "dock" : "sidebar"}>
      {DESTINATIONS.map((destination) => (
        <DestinationShortcut
          key={destination.id}
          destination={destination}
          onSelect={onSelect}
        />
      ))}

      {showsSidebar ? (
        <div
          className={styles.sidebarColumn}
          id="shell-sidebar"
          inert={covered}
          aria-hidden={covered ? true : undefined}
        >
          <Sidebar
            selected={navigation.destination}
            buildTool={navigation.buildTool}
            activity={activity}
            onSelect={onSelect}
            onRingside={onRingside}
          />
        </div>
      ) : null}

      <div className={styles.body} inert={covered} aria-hidden={covered ? true : undefined}>
        {showsDock ? null : (
          // §7.3: the 620–1000 band renders detail-only, so the toggle has to be
          // persistent rather than a hover affordance — it is the only way back
          // to the sections in that band.
          <header className={styles.header}>
            <button
              type="button"
              data-testid="shell.sidebarToggle"
              className={styles.toggle}
              aria-expanded={showsSidebar}
              aria-controls="shell-sidebar"
              onClick={() => {
                setSidebar(toggleSidebarVisibility);
              }}
            >
              {showsSidebar ? "Hide sections" : "Show sections"}
            </button>
            <div className={styles.wordmark}>
              <span className={styles.wordmarkEyebrow}>{WORDMARK_EYEBROW}</span>
              <span className={styles.wordmarkTitle}>{WORDMARK_TITLE}</span>
            </div>
          </header>
        )}

        <main className={styles.stack} id="main">
          {DESTINATIONS.map((destination) => {
            const Root = DESTINATION_ROOTS[destination.id];
            return (
              <DestinationRoot
                key={destination.id}
                destination={destination.id}
                title={destination.title}
                active={destination.id === navigation.destination}
              >
                <Suspense fallback={<RouteFallback />}>
                  <Root />
                </Suspense>
              </DestinationRoot>
            );
          })}
        </main>

        {showsDock ? <Dock selected={navigation.destination} onSelect={onSelect} /> : null}
      </div>

      {FullViewport === undefined ? null : (
        // The live surface covers the shell rather than replacing it: the four
        // roots underneath keep their scroll offsets and drafts while a workout
        // runs.
        <div className={styles.fullViewport} data-testid="shell.fullViewport">
          <Suspense fallback={<RouteFallback />}>
            <FullViewport />
          </Suspense>
        </div>
      )}
    </div>
  );
}
